import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MessageDialogCutomizadoComponent } from '../components/genericos/message-dialog-cutomizado/message-dialog-cutomizado.component';
import { IErrorMessage } from '../interfaces/ierror-message';
import { JwtService } from '../jwt.service';
import { PedidoManager } from 'src/app/managers/pedido-manager';

/**
 * Tamanho máximo plausível para uma mensagem de negócio. Acima disso o corpo não é
 * mensagem — é página de erro, dump ou payload inesperado — e não deve ir para a tela.
 *
 * Dimensionado com folga sobre o medido: a maior mensagem de negócio alcançável pelos
 * endpoints do B2B tem cerca de 145 caracteres, e as do caminho do gravarBusiness ficam
 * entre 66 e 75. A margem cobre mensagens que agregam várias validações com quebra de linha.
 */
const TAMANHO_MAXIMO_MENSAGEM = 400;

/**
 * Prefixos que denunciam documento estruturado em vez de mensagem de negócio, quando o
 * documento é o corpo inteiro: página de erro HTML do container/proxy, ou JSON serializado
 * usado como mensagem de exceção (ex.: PedidoVendaRegra faz
 * `throw new Exception(new Gson().toJson(restricoesFrete))`).
 */
const PREFIXOS_NAO_MENSAGEM = ['<', '{', '['];

/**
 * Aberturas de documento estruturado procuradas em QUALQUER posição, não só no início.
 * Necessário porque vários pontos do backend prependem um prefixo próprio antes da mensagem
 * da exceção — ex.: PixRegra faz `"Erro ao consultar servico de integracao pix: " +
 * e.getMessage()` —, de modo que um JSON ou HTML embutido não começaria a string.
 */
const DOCUMENTOS_EMBUTIDOS = ['{"', '[{', '<html', '<!doctype'];

/**
 * Corpos que representam ausência de mensagem. Ocorrem quando `e.getMessage()` é nulo e o
 * endpoint declara `responseType: 'text'`: sem parse, o literal do Gson chega como texto.
 */
const CORPOS_SEM_MENSAGEM = ['null', 'undefined'];

/**
 * Marcadores de erro técnico de persistência. Quando aparecem no corpo, o texto não é
 * mensagem de negócio — é retorno de driver/ORM — e não deve ir para a tela do comprador.
 * Vários pontos do backend concatenam `e.getMessage()` da exceção capturada sobre um
 * prefixo próprio (ex.: BannerRegra, PainelRegra, ClienteVendedorRegra), e é assim que
 * texto de SQL chega até aqui.
 *
 * Comparados em minúsculas, por isso já estão em minúsculas. São frases completas de
 * propósito: `viola a restrição` em vez de `restrição`, para não descartar mensagem de
 * negócio que use a palavra isolada.
 *
 * Cada marcador tem um teste com amostra isolada, de modo que remover qualquer entrada desta
 * lista quebra exatamente um teste. `psqlexception` foi removido por ser inalcançável:
 * a string contém `sqlexception`, que já está na lista.
 *
 * `br.com.` é procurado SEM o prefixo `at ` de propósito: a mensagem de helpful NullPointer
 * do JDK 21 cita a classe interna sem estar dentro de stack trace — ex.: `Cannot invoke
 * "br.com.flexsmart.model.cliente.Cliente.getCdCliente()" because "cliente" is null` —, e com
 * `at br.com.` essa referência escapava do filtro e ia para a tela do comprador. Não colide
 * com domínio brasileiro, que é `.com.br` e não `br.com.`.
 *
 * `cannot invoke ` cobre a mesma helpful NullPointer quando a classe citada não é nossa
 * (ex.: `Cannot invoke "java.lang.String.length()"`), caso em que `br.com.` não casaria.
 *
 * MITIGAÇÃO, não solução: o certo é o backend não expor o detalhe da exceção de
 * persistência. Ver seção 12 de tarefa_68659.md.
 */
const MARCADORES_TECNICOS = [
  'sqlexception',
  'sqlgrammarexception',
  'constraintviolationexception',
  'org.postgresql',
  'org.hibernate',
  'jdbc',
  'could not execute',
  'could not extract',
  'viola a restrição',
  'violates unique constraint',
  'valor nulo na coluna',
  'null value in column',
  'br.com.',
  'cannot invoke ',
  'exception:'
];

/**
 * Marcadores de erro do PostgreSQL procurados em **qualquer posição** (os wrappers do backend
 * prependem um prefixo próprio, então nunca ficam no início) e com a **caixa preservada**:
 * o driver emite em maiúsculas, e uma mensagem de negócio que comece com "Erro:" não deve
 * ser descartada.
 */
const MARCADORES_ERRO_BANCO_COM_CAIXA = ['ERRO:', 'ERROR:'];

@Injectable({
  providedIn: 'root'
})

export class HttpErrorMessageService {

  iErrorMessage: IErrorMessage;
  isExecutou = false;
  constructor(
    private dialog: MatDialog,
    private jwtService: JwtService,
    private pedidoManager: PedidoManager
  ){}

  /**
   * Retorna a mensagem de negócio devolvida pela API, ou null quando o corpo não for
   * uma mensagem exibível ao usuário.
   *
   * Os controllers do flexsmart-web respondem com `new Gson().toJson(e.getMessage())`.
   * Nos endpoints com responseType 'json' o HttpClient já entrega a string parseada; nos
   * que declaram `responseType: 'text' as 'json'` (PedidoService.gravarVendaPerdida e
   * aprovarOrcamento) não há parse, e o corpo chega como JSON string literal — com as
   * aspas. Por isso o desempacotamento em desempacotarJson().
   *
   * ATENÇÃO ao comportamento do HttpXhrBackend: em resposta de ERRO o Angular NÃO embrulha
   * corpo não-parseável em { error, text }; esse embrulho só acontece em resposta 2xx. Num
   * erro, o corpo que falha no JSON.parse chega como string crua. Ou seja, a página HTML de
   * erro do Wildfly/proxy também chega aqui como string. É por isso que existem a validação
   * de forma abaixo e o repasse restrito por status em getErrorMessage().
   */
  getMensagemApi(error: HttpErrorResponse): string | null {
    const mensagem = this.getCorpoTexto(error);

    if (mensagem === null || mensagem === '' || CORPOS_SEM_MENSAGEM.indexOf(mensagem) !== -1)
      return null;

    if (mensagem.length > TAMANHO_MAXIMO_MENSAGEM)
      return null;

    if (PREFIXOS_NAO_MENSAGEM.some(prefixo => mensagem.startsWith(prefixo)))
      return null;

    if (this.temDocumentoEmbutido(mensagem) || this.temMarcadorTecnico(mensagem))
      return null;

    return mensagem;
  }

  /**
   * Indica se a mensagem embute documento estruturado — JSON ou HTML — em qualquer posição.
   */
  private temDocumentoEmbutido(mensagem: string): boolean {
    const minuscula = mensagem.toLowerCase();

    return DOCUMENTOS_EMBUTIDOS.some(abertura => minuscula.indexOf(abertura) !== -1);
  }

  /**
   * Corpo da resposta como texto, já com o JSON string literal desempacotado, ou null quando
   * o corpo não é string.
   *
   * É o valor contra o qual TODA comparação de conteúdo deve ser feita — inclusive as
   * comparações literais de getErrorMessage() e openErrorMessageDialog(). Os endpoints que
   * declaram `responseType: 'text'` entregam o corpo com as aspas do Gson, então comparar o
   * corpo cru faria os tratamentos especiais (redirect de sessão, efeito colateral do pedido
   * indisponível) falharem justamente nesses endpoints.
   */
  private getCorpoTexto(error: HttpErrorResponse): string | null {
    if (typeof error.error !== 'string')
      return null;

    return this.desempacotarJson(error.error.trim());
  }

  /**
   * Indica se a mensagem carrega retorno de driver/ORM em vez de texto de negócio.
   */
  private temMarcadorTecnico(mensagem: string): boolean {
    const minuscula = mensagem.toLowerCase();

    if (MARCADORES_TECNICOS.some(marcador => minuscula.indexOf(marcador) !== -1))
      return true;

    return MARCADORES_ERRO_BANCO_COM_CAIXA.some(marcador => mensagem.indexOf(marcador) !== -1);
  }

  /**
   * Desempacota um JSON string literal ("mensagem"), devolvendo o texto original quando o
   * corpo não estiver nesse formato.
   */
  private desempacotarJson(corpo: string): string {
    if (corpo.length < 2 || !corpo.startsWith('"') || !corpo.endsWith('"'))
      return corpo;

    try {
      const conteudo = JSON.parse(corpo);
      return typeof conteudo === 'string' ? conteudo.trim() : corpo;
    } catch {
      return corpo;
    }
  }

  getErrorMessage(error: HttpErrorResponse): IErrorMessage {

    /*
     * A mensagem da API é repassada apenas nos status 400 e 403 — os únicos em que o corpo
     * é comprovadamente produzido por código nosso: o catch dos controllers e o abortWith do
     * CORSInterceptador, ambos serializando com Gson. Nos demais status o corpo vem do
     * container ou do proxy (página HTML de erro), não é mensagem de negócio, e por isso os
     * textos de infraestrutura são mantidos.
     */
    const corpo = this.getCorpoTexto(error);
    const mensagemApi = this.getMensagemApi(error);

    switch (error.status) {
      case 0:
        return this.iErrorMessage = { message: 'Não foi possível conectar ao servidor. Você será redirecionado para o login. Algumas informações podem ter sido perdidas!',
                                 redirect: true }
      case 400:
        if(corpo === "O pedido não está mais disponível!"){
          this.pedidoManager.pedidoIndisponivel();
          return this.iErrorMessage = { message: "Pedido já salvo no servidor, por favor verifique seus pedidos." }
        }
        return this.iErrorMessage = { message: mensagemApi ?? 'Requisição inválida. Verifique os parâmetros enviados.' }
      case 401:
        return this.iErrorMessage = { message: 'Usuário não logado!',
                                      redirect: true }
      case 403:
        if(corpo === "Usuario não logado!"){
          return this.iErrorMessage = { message: 'Usuário não logado!', redirect: true }
        }else{
          return this.iErrorMessage = { message: mensagemApi ?? 'Acesso negado. Você não tem permissão para acessar essa página.' }
        }
      case 404:
        return this.iErrorMessage = { message: 'Página não encontrada.'}
      case 408:
        return this.iErrorMessage = { message: 'Tempo limite da requisição excedido. Verifique sua conexão.' }
      case 429:
        return this.iErrorMessage = { message: 'Muitas requisições. Aguarde um momento e tente novamente.' }
      case 451:
        return this.iErrorMessage = { message: 'Acesso não disponível por motivos legais.' }
      default:
        return this.iErrorMessage = { message: 'Erro desconhecido. Você será redirecionado para o login. Algumas informações podem ter sido perdidas!',
                                      redirect: true }
    }
  }
 
  openErrorMessageDialog(error: HttpErrorResponse) {
    if (!this.isExecutou) {
      this.isExecutou = true;
      if(this.getCorpoTexto(error) === 'Erro ao Autenticar, verifique seu usuário/senha'){
        this.iErrorMessage = {message:'Erro ao Autenticar, verifique seu usuário/senha'}
      } else{
        this.iErrorMessage = this.getErrorMessage(error); 
      }
      const message = this.iErrorMessage.message;
      const redirect = this.iErrorMessage.redirect;
      const dialogRef = this.dialog.open(MessageDialogCutomizadoComponent, {
        width: '300px',
        disableClose: true,
        data: { message: message }
      });

      dialogRef.afterClosed().subscribe(() => {
        if(redirect)
          this.jwtService.logout();
        this.isExecutou = false;
      });
    }
  }  

}